import { baseCMSApi } from '@/shared/api/base-cms-api';
import { CMS_ENDPOINTS } from '@/shared/api/endpoints';
import type { PlatformFeature } from '@/shared/cms/shared';

import type { PlanPrice } from '../model/types/plan-price';

// The real subscription tiers. `platform-features` also holds a-la-carte
// features (e.g. Pattern Studio) that carry a price but are not full plans;
// we keep only these slugs so the calculator offers meaningful choices.
const SUBSCRIPTION_PLAN_SLUGS = ['basic', 'premium', 'pro'] as const;

function toPlanPrice(feature: PlatformFeature): PlanPrice {
    return {
        id: feature.id,
        title: feature.title || feature.slug,
        slug: feature.slug,
        monthlyPrice: feature.monthlyPrice ?? 0,
        yearlyPrice: feature.yearlyPrice ?? 0,
    };
}

const planPriceApi = baseCMSApi
    .enhanceEndpoints({ addTagTypes: ['PlanPrices'] })
    .injectEndpoints({
        endpoints: (build) => ({
            getPlanPrices: build.query<PlanPrice[], { locale: string }>({
                providesTags: ['PlanPrices'],
                query: (params) => ({
                    url: CMS_ENDPOINTS.getPlatformFeatures(),
                    method: 'GET',
                    params: {
                        locale: params.locale,
                        depth: 0,
                        limit: 50,
                        sort: 'monthlyPrice',
                    },
                }),
                transformResponse: (response: unknown): PlanPrice[] => {
                    if (typeof response !== 'object' || response === null) {
                        return [];
                    }

                    const { docs } = response as { docs?: unknown };

                    if (!Array.isArray(docs)) {
                        return [];
                    }

                    return docs
                        .map((doc) => toPlanPrice(doc as PlatformFeature))
                        .filter(
                            (plan) =>
                                plan.monthlyPrice > 0 &&
                                SUBSCRIPTION_PLAN_SLUGS.includes(
                                    plan.slug as (typeof SUBSCRIPTION_PLAN_SLUGS)[number],
                                ),
                        );
                },
            }),
        }),
    });

export const { useGetPlanPricesQuery } = planPriceApi;
